import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useOrg } from '../../../shared/contexts/OrgContext';
import { extractOrgIdFromPath } from '../../../shared/utils/organizationUtils';
import { socialMediaService, SocialMediaUpload } from '../../../shared/services/socialMediaService';
import SocialMediaUploadsTable from '../components/SocialMediaUploadsTable';
import SocialMediaUploadDrawer from '../components/SocialMediaUploadDrawer';
import Toast from '../../../shared/components/ui/Toast';
import IconButton from '../../../shared/components/ui/IconButton';
import Modal from '../../../shared/components/ui/Modal';

interface ToastState {
  isVisible: boolean;
  type: 'success' | 'error' | 'info';
  message: string;
}

interface SocialMediaUploadsPageProps {
  globalSearch?: string;
}

const SocialMediaUploadsPage: React.FC<SocialMediaUploadsPageProps> = ({ globalSearch = '' }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentOrganization } = useOrg();
  const orgIdFromPath = extractOrgIdFromPath(location.pathname);
  const orgId = currentOrganization?.id || orgIdFromPath || 'default';

  const [uploads, setUploads] = useState<SocialMediaUpload[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [toast, setToast] = useState<ToastState>({
    isVisible: false,
    type: 'info',
    message: '',
  });
  const [deleteUploadId, setDeleteUploadId] = useState<string | null>(null);
  const [selectedUploadId, setSelectedUploadId] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Check if we should open drawer from navigation state or URL params
  useEffect(() => {
    const state = location.state as { openDrawer?: boolean; uploadId?: string } | null;
    const urlParams = new URLSearchParams(location.search);
    const uploadIdFromUrl = urlParams.get('uploadId');
    
    if (state?.openDrawer && state.uploadId) {
      setSelectedUploadId(state.uploadId);
      setIsDrawerOpen(true);
      // Clear state to prevent reopening on refresh
      navigate(location.pathname, { replace: true, state: {} });
    } else if (uploadIdFromUrl) {
      setSelectedUploadId(uploadIdFromUrl);
      setIsDrawerOpen(true);
      // Remove query param
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, location.search, location.pathname, navigate]);

  // Load uploads
  const loadUploads = async (showLoading = true) => {
    if (showLoading) {
      setIsLoading(true);
    }
    try {
      const result = await socialMediaService.getUploadsForOrg(orgId);
      if (result.success && result.data) {
        setUploads(result.data);
      } else {
        if (showLoading) {
          setToast({
            isVisible: true,
            type: 'error',
            message: result.error || 'Failed to load uploads',
          });
        }
        setUploads([]);
      }
    } catch (error) {
      console.error('Error loading uploads:', error);
      if (showLoading) {
        setToast({
          isVisible: true,
          type: 'error',
          message: 'Failed to load uploads',
        });
      }
      setUploads([]);
    } finally {
      if (showLoading) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (orgId) {
      loadUploads(true);
    }
  }, [orgId]);

  // Poll for status updates every 5 seconds (without showing loading state)
  useEffect(() => {
    if (!orgId) return;

    const interval = setInterval(() => {
      loadUploads(false); // Don't show loading spinner on polling
    }, 5000);

    return () => clearInterval(interval);
  }, [orgId]);

  const handleRowClick = (uploadId: string) => {
    setSelectedUploadId(uploadId);
    setIsDrawerOpen(true);
  };

  const handleViewClick = (uploadId: string) => {
    setSelectedUploadId(uploadId);
    setIsDrawerOpen(true);
  };

  const handleDeleteClick = (uploadId: string) => {
    setDeleteUploadId(uploadId);
  };

  const handleConfirmDelete = async () => {
    if (!deleteUploadId) return;

    try {
      const result = await socialMediaService.deleteUpload(deleteUploadId);
      if (result.success) {
        setToast({
          isVisible: true,
          type: 'success',
          message: 'Upload deleted successfully',
        });
        loadUploads();
      } else {
        setToast({
          isVisible: true,
          type: 'error',
          message: result.error || 'Failed to delete upload',
        });
      }
    } catch (error) {
      console.error('Error deleting upload:', error);
      setToast({
        isVisible: true,
        type: 'error',
        message: 'Failed to delete upload',
      });
    } finally {
      setDeleteUploadId(null);
    }
  };

  const RefreshIcon = () => (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M4.93 4.93a10 10 0 0114.14 0l.35.36V3a1 1 0 012 0v6a1 1 0 01-1 1h-6a1 1 0 010-2h3.59l-.35-.36a8 8 0 00-11.32 0 1 1 0 11-1.41-1.41zM19.07 19.07a10 10 0 01-14.14 0l-.35-.36V21a1 1 0 01-2 0v-6a1 1 0 011-1h6a1 1 0 010 2H6.59l.35.36a8 8 0 0011.32 0 1 1 0 111.41 1.41z" />
    </svg>
  );

  return (
    <div className="p-4 sm:p-6">
      {/* Refresh Button */}
      <div className="mb-4 flex justify-end">
        <IconButton
          onClick={() => loadUploads(true)}
          variant="ghost"
          size="sm"
          className="border border-gray-200 text-gray-600 hover:text-gray-900 rounded-full bg-white"
          title="Refresh"
        >
          <RefreshIcon />
        </IconButton>
      </div>

      {/* Table */}
      <SocialMediaUploadsTable
        uploads={uploads}
        isLoading={isLoading}
        onRowClick={handleRowClick}
        onViewClick={handleViewClick}
        onDeleteClick={handleDeleteClick}
        searchQuery={globalSearch}
      />

      {/* Drawer */}
      <SocialMediaUploadDrawer
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          setSelectedUploadId(null);
        }}
        uploadId={selectedUploadId}
        onDelete={(uploadId) => {
          handleDeleteClick(uploadId);
        }}
        onRetry={() => {
          loadUploads();
        }}
      />

      {/* Delete Confirmation Modal */}
      {deleteUploadId && (
        <Modal
          isOpen={true}
          onClose={() => setDeleteUploadId(null)}
          title="Delete Upload"
        >
          <div className="p-6 space-y-4">
            <p className="text-sm text-gray-700">
              Are you sure you want to delete this upload?
            </p>
            <p className="text-xs text-gray-500">
              This action cannot be undone. The upload record will be permanently deleted.
            </p>
            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setDeleteUploadId(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
              >
                Delete Upload
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Toast */}
      {toast.isVisible && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={toast.isVisible}
          onClose={() => setToast({ ...toast, isVisible: false })}
        />
      )}
    </div>
  );
};

export default SocialMediaUploadsPage;

